import { t } from '@procumi/shared';
import { useState } from 'react';
import { I, type PATHS } from '../components/dashboard/shared';
import { SupportPanel } from '../components/SupportPanel';
import type { Route } from '../routes';

/**
 * „Centru de ajutor”: ghiduri scurte pentru pașii de zi cu zi și, alături,
 * întrebările către echipa Procumi (tichetele, din 2026-10-29; răspundem din consolă).
 */
interface Guide {
  key: string;
  icon: keyof typeof PATHS;
  tone: string;
  title: string;
  steps: string[];
  go?: { label: string; route: Route };
}

const GUIDES: Guide[] = [
  {
    key: 'rfq',
    icon: 'rfq',
    tone: 'blue',
    title: t('Cum fac o cerere de ofertă'),
    steps: [
      t('Apasă „Cerere nouă”, alege șantierul și scrie materialele, cu cantitatea și unitatea de măsură.'),
      t('Alege furnizorii cărora le trimiți cererea și termenul până la care să răspundă.'),
      t('Trimite: fiecare furnizor primește un e-mail cu un link unde completează oferta.'),
    ],
    go: { label: t('Cerere nouă'), route: { view: 'new' } },
  },
  {
    key: 'supplier',
    icon: 'send',
    tone: 'night',
    title: t('Cum răspund furnizorii'),
    steps: [
      t('Furnizorii nu au nevoie de cont: deschid linkul din e-mail și trec prețurile pe fiecare poziție.'),
      t('Ofertele ajung singure în cerere; pe panou vezi cine a răspuns și cine întârzie.'),
      t('Dacă un furnizor tace, trimite-i un follow-up din panou sau din cerere.'),
    ],
  },
  {
    key: 'compare',
    icon: 'compare',
    tone: 'blue',
    title: t('Cum compar ofertele și aleg'),
    steps: [
      t('Deschide cererea: ofertele stau una lângă alta, pe poziții, cu cel mai mic preț scos în evidență.'),
      t('Vezi și ofertele parțiale sau cele care expiră, ca să nu compari mere cu pere.'),
      t('Alege furnizorul; economia față de următoarea ofertă se socotește singură.'),
    ],
    go: { label: t('Cererile de ofertă'), route: { view: 'rfqs', state: 'open' } },
  },
  {
    key: 'order',
    icon: 'box',
    tone: 'night',
    title: t('Comanda și aprobarea'),
    steps: [
      t('După alegere apasă „Fă comanda”: comanda se completează din ofertă.'),
      t('Dacă firma are reguli de aprobare, comanda așteaptă aprobatorul înainte să plece.'),
      t('Livrările se trec pe comandă, ca să știi ce a ajuns pe șantier.'),
    ],
    go: { label: t('Comenzile'), route: { view: 'orders' } },
  },
  {
    key: 'requisition',
    icon: 'clipboard',
    tone: 'slate',
    title: t('Necesarul de pe șantier'),
    steps: [
      t('Oamenii de pe șantier trimit necesarul din aplicație, cu materialele și data la care le trebuie.'),
      t('Achizițiile îl preiau și fac din el o cerere de ofertă, fără să rescrie liniile.'),
    ],
    go: { label: t('Necesarul'), route: { view: 'requisitions' } },
  },
  {
    key: 'suppliers',
    icon: 'people',
    tone: 'night',
    title: t('Furnizorii tăi'),
    steps: [
      t('Adaugă furnizorii unul câte unul sau importă-i dintr-un tabel.'),
      t(
        'Fiecare furnizor are istoricul lui: la ce a răspuns, cât de repede și cât de des a avut cel mai bun preț.',
      ),
    ],
    go: { label: t('Furnizorii'), route: { view: 'suppliers' } },
  },
  {
    key: 'team',
    icon: 'site',
    tone: 'blue',
    title: t('Echipa, rolurile și aprobările'),
    steps: [
      t('În „Contul firmei” inviți colegii și le dai rolul potrivit.'),
      t('Tot acolo stabilești cine aprobă comenzile și de la ce valoare.'),
    ],
    go: { label: t('Contul firmei'), route: { view: 'company', tab: 'team' } },
  },
  {
    key: 'prices',
    icon: 'tag',
    tone: 'night',
    title: t('Prețurile și analiza'),
    steps: [
      t('Fiecare preț primit rămâne în istoric: vezi ce ai plătit, la cine și când.'),
      t('În „Analiză” vezi cât ai cumpărat, cât ai economisit și cum lucrează furnizorii.'),
    ],
    go: { label: t('Prețurile'), route: { view: 'prices' } },
  },
];

export function HelpPage({ go, ticket }: { go: (r: Route) => void; ticket?: string }) {
  const [open, setOpen] = useState<string>('rfq');

  return (
    <div className="help">
      <div className="head">
        <div>
          <h1>{t('Centru de ajutor')}</h1>
          <p>{t('Pașii de zi cu zi, pe scurt. Dacă tot nu merge, scrie-ne și te ajutăm.')}</p>
        </div>
      </div>

      <div className="help-grid">
        <section className="help-guides" aria-label={t('Ghiduri')}>
          {GUIDES.map((g) => {
            const on = open === g.key;
            return (
              <article key={g.key} className={`help-g${on ? ' on' : ''}`}>
                <h2>
                  <button type="button" aria-expanded={on} onClick={() => setOpen(on ? '' : g.key)}>
                    <span className={`db-ico ${g.tone}`}>
                      <I d={g.icon} size={16} />
                    </span>
                    <span className="help-t">{t(g.title)}</span>
                    <span className="help-chev" aria-hidden="true">
                      <I d="down" size={14} />
                    </span>
                  </button>
                </h2>
                {on ? (
                  <div className="help-b">
                    <ol>
                      {g.steps.map((s) => (
                        <li key={s}>{t(s)}</li>
                      ))}
                    </ol>
                    {g.go ? (
                      <button type="button" className="btn small" onClick={() => go(g.go!.route)}>
                        {g.go.label}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>

        <SupportPanel openId={ticket} />
      </div>
    </div>
  );
}
